// src/views/condominos/CondominosList.jsx
import React, { useState, useEffect, useCallback } from "react";
import condominosService from "../../services/condominosService";
import { useAuth } from "../../contexts/AuthContext";
import CondominoModal from "./CondominoModal";
import DeleteModal from "../../components/ui/DeleteModal";
import ImportarCondominosModal from "./ImportarCondominosModal";
import AniversariantesModal from "./AniversariantesModal";
import { gerarFichaPdf } from "./fichaPdfHelper";

const PER_PAGE = 10;

export default function CondominosList() {
  const { podeEditar } = useAuth();
  const canEdit = podeEditar("condominos");

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [pagination, setPagination] = useState({ total:0, totalPages:1 });
  const [stats, setStats]           = useState({ total:0, ativos:0, inativos:0, pf:0, pj:0, proprietarios:0, inquilinos:0, semVinculo:0 });

  // Filtros
  const [search, setSearch]             = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPessoa, setFilterPessoa] = useState("");
  const [filterVinculo, setFilterVinculo] = useState("");
  const [page, setPage]                 = useState(1);

  const [showModal, setShowModal]     = useState(false);
  const [editData, setEditData]       = useState(null);
  const [showDelete, setShowDelete]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [modalError, setModalError]   = useState(null);
  const [showImport, setShowImport]   = useState(false);
  const [showAniv, setShowAniv]       = useState(false);
  const [exportingId, setExportingId] = useState(null);

  const handleExportarFicha = async (c) => {
    setExportingId(c.id);
    try {
      const r = await condominosService.fichaPdf(c.id);
      gerarFichaPdf(r.data);
    } catch(e) { setError(e.message||"Erro ao gerar ficha."); }
    finally { setExportingId(null); }
  };

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await condominosService.listar({
        search, status:filterStatus, tipoPessoa:filterPessoa,
        semVinculo: filterVinculo === "sem" ? "1" : "",
        page, limit:PER_PAGE,
      });
      setData(res.data);
      setPagination(res.pagination);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterPessoa, filterVinculo, page]);

  const carregarStats = useCallback(async () => {
    try {
      const r = await condominosService.stats();
      if (r?.data) setStats(r.data);
    } catch {}
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarStats(); }, []);

  const totalPages = pagination.totalPages || 1;

  const handleSave = async (form) => {
    setSaving(true); setModalError(null);
    try {
      if (form.id) await condominosService.atualizar(form.id, form);
      else         await condominosService.criar(form);
      setShowModal(false);
      await carregar();
      carregarStats();
    } catch(err) { setModalError(err.message||"Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await condominosService.deletar(deleteTarget.id);
      setShowDelete(false); setDeleteTarget(null);
      await carregar();
      carregarStats();
    } catch(err) { setError(err.message); }
    finally { setDeleting(false); }
  };

  const handleStatus = async (c) => {
    try {
      await condominosService.alterarStatus(c.id, !c.inativo);
      await carregar();
      carregarStats();
    } catch(err) { setError(err.message||"Erro ao alterar status."); }
  };

  // Filtros rápidos pelos cards (clicáveis)
  const aplicarFiltroCard = (filtro) => {
    setPage(1);
    setFilterStatus(""); setFilterPessoa(""); setFilterVinculo("");
    if (filtro === "ativos") setFilterStatus("ativo");
    else if (filtro === "inativos") setFilterStatus("inativo");
    else if (filtro === "pf") setFilterPessoa("Fisica");
    else if (filtro === "pj") setFilterPessoa("Juridica");
    else if (filtro === "sem") setFilterVinculo("sem");
  };

  const cardsKpi = [
    { key:"total",      icon:"bi-people",         cls:"blue",   val:stats.total,         label:"Total" },
    { key:"ativos",     icon:"bi-check-circle",   cls:"green",  val:stats.ativos,        label:"Ativos" },
    { key:"pj",         icon:"bi-building",       cls:"purple", val:stats.pj,            label:"Pessoa Jurídica" },
    { key:"sem",        icon:"bi-exclamation-circle", cls:"orange", val:stats.semVinculo, label:"Sem unidade" },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-people me-2" style={{ color:"var(--primary)",fontSize:22 }}/>Condôminos</h1>
          <p className="page-subtitle">Cadastro global por cliente — vincule a múltiplas unidades em qualquer condomínio</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <button className="btn btn-outline-secondary" onClick={()=>setShowAniv(true)} title="Aniversariantes do mês">
            <i className="bi bi-cake2 me-1"/> Aniversariantes
          </button>
          <button className="btn btn-outline-secondary" onClick={()=>setShowImport(true)} disabled={!canEdit} title="Importar condôminos via CSV">
            <i className="bi bi-upload me-1"/> Importar CSV
          </button>
          <button className="btn btn-primary" onClick={()=>{ setEditData(null); setShowModal(true); }} disabled={!canEdit}>
            <i className="bi bi-plus-lg me-1"/> Novo Condômino
          </button>
        </div>
      </div>

      {/* Stats clicáveis */}
      <div className="row g-3 mb-4">
        {cardsKpi.map(s => (
          <div className="col-6 col-md-3" key={s.key}>
            <div className="stat-card" onClick={()=>aplicarFiltroCard(s.key)}
              style={{cursor:"pointer", transition:"transform .15s"}}
              onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
              onMouseLeave={e=>e.currentTarget.style.transform=""}>
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`}/></div>
              <div>
                <div className="stat-value">{s.val ?? 0}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Linha de KPIs secundários compacta */}
      <div className="d-flex flex-wrap gap-3 mb-3" style={{fontSize:12, color:"var(--gray-600)"}}>
        <span><i className="bi bi-house-check me-1 text-success"/>Proprietários: <strong>{stats.proprietarios||0}</strong></span>
        <span><i className="bi bi-person-check me-1" style={{color:"#FB923C"}}/>Inquilinos: <strong>{stats.inquilinos||0}</strong></span>
        <span><i className="bi bi-person me-1 text-primary"/>Pessoa Física: <strong>{stats.pf||0}</strong></span>
        <span><i className="bi bi-x-circle me-1 text-secondary"/>Inativos: <strong>{stats.inativos||0}</strong></span>
      </div>

      <div className="card">
        <div className="card-header-custom">
          <div className="search-wrapper">
            <i className="bi bi-search"/>
            <input className="search-input" placeholder="Buscar por nome, CPF/CNPJ, e-mail, celular..." value={search}
              onChange={e=>{ setSearch(e.target.value); setPage(1); }}/>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterStatus} onChange={e=>{ setFilterStatus(e.target.value); setPage(1); }}>
              <option value="">Status: Todos</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterPessoa} onChange={e=>{ setFilterPessoa(e.target.value); setPage(1); }}>
              <option value="">Pessoa: Todos</option>
              <option value="Fisica">Pessoa Física</option>
              <option value="Juridica">Pessoa Jurídica</option>
            </select>
            <select className="form-select form-select-sm" style={{ width:"auto",fontSize:13 }} value={filterVinculo} onChange={e=>{ setFilterVinculo(e.target.value); setPage(1); }}>
              <option value="">Vínculo: Todos</option>
              <option value="sem">Sem vínculo</option>
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={()=>{carregar();carregarStats();}} title="Atualizar"><i className="bi bi-arrow-clockwise"/></button>
          </div>
        </div>

        {error && <div className="alert alert-danger m-3 mb-0" style={{ fontSize:13 }}><i className="bi bi-exclamation-triangle-fill me-2"/>{error}</div>}

        <div className="table-responsive">
          <table className="table table-custom">
            <thead>
              <tr><th>#</th><th>Nome</th><th>Tipo</th><th>CPF / CNPJ</th><th>Contato</th><th>Condomínios vinculados</th><th>Status</th><th style={{ width:130 }}>Ações</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-5">
                  <span className="spinner-border spinner-border-sm text-primary me-2"/><span style={{ color:"var(--gray-500)" }}>Carregando...</span>
                </td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-5" style={{ color:"var(--gray-400)" }}>
                  <i className="bi bi-search" style={{ fontSize:32,display:"block",marginBottom:8 }}/>Nenhum condômino encontrado
                </td></tr>
              ) : data.map((c, idx) => (
                <tr key={c.id}>
                  <td style={{ color:"var(--gray-400)",fontSize:12 }}>{(page-1)*PER_PAGE+idx+1}</td>
                  <td>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{
                        width:32,height:32,borderRadius:"50%",flexShrink:0,
                        background: c.avatar ? `url(${c.avatar}) center/cover` : "#E2E8F0",
                        display:"flex",alignItems:"center",justifyContent:"center"
                      }}>
                        {!c.avatar && <i className="bi bi-person" style={{color:"var(--gray-400)"}}/>}
                      </div>
                      <div>
                        <div style={{ fontWeight:600,color:"var(--gray-800)" }}>{c.nmCompleto}</div>
                        <div style={{ fontSize:11.5,color:"var(--gray-400)" }}>
                          {c.tpPessoa==="Fisica"?"Pessoa Física":"Pessoa Jurídica"}
                          {c.tags && (c.tags||"").split(",").map(t=>t.trim()).filter(Boolean).slice(0,3).map((t,i)=>(
                            <span key={i} className="badge text-bg-light border ms-1" style={{fontSize:9}}>{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {c.tpVinculoPrincipal ? (
                      <span className="badge-tipo" style={{ background: c.tpVinculoPrincipal==="Inquilino"?"#FFF7ED":"#EFF6FF", color:c.tpVinculoPrincipal==="Inquilino"?"#C2410C":"var(--primary)" }}>
                        {c.tpVinculoPrincipal==="Inquilino"?"Inquilino":(c.tpVinculoPrincipal==="Proprietario"?"Proprietário":c.tpVinculoPrincipal)}
                      </span>
                    ) : (
                      <span style={{fontSize:11.5,color:"var(--gray-400)"}}>—</span>
                    )}
                  </td>
                  <td style={{ fontFamily:"monospace",fontSize:12.5 }}>{c.cpf_cnpj||"-"}</td>
                  <td style={{ fontSize:12.5 }}>
                    {c.email && <div style={{color:"var(--gray-700)"}}><i className="bi bi-envelope me-1"/>{c.email}</div>}
                    {(c.celular||c.telefone) && <div style={{color:"var(--gray-500)"}}><i className="bi bi-telephone me-1"/>{c.celular||c.telefone}</div>}
                    {!c.email && !c.celular && !c.telefone && <span style={{color:"var(--gray-400)"}}>—</span>}
                  </td>
                  <td style={{fontSize:12,color:"var(--gray-600)",maxWidth:200}}>
                    {c.condominiosVinculados ? (
                      <span title={c.condominiosVinculados} style={{display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        <i className="bi bi-buildings me-1"/>{c.condominiosVinculados}
                        {c.qtdUnidades > 0 && <span className="badge bg-light text-dark ms-2" style={{fontSize:10}}>{c.qtdUnidades} und</span>}
                      </span>
                    ) : (
                      <span style={{color:"var(--gray-400)",fontSize:12}}>Sem unidade vinculada</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge-status ${c.inativo?"badge-inativo":"badge-ativo"}`} onClick={()=>canEdit && handleStatus(c)} style={{cursor:canEdit?"pointer":"default"}}>
                      <i className="bi bi-circle-fill" style={{ fontSize:7 }}/>{c.inativo?"Inativo":"Ativo"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar" onClick={()=>{ setEditData(c); setShowModal(true); }} disabled={!canEdit}><i className="bi bi-pencil"/></button>
                      <button className="btn btn-outline-primary btn-sm btn-icon" title="Exportar ficha (PDF)" onClick={()=>handleExportarFicha(c)} disabled={exportingId===c.id}>
                        {exportingId===c.id ? <span className="spinner-border spinner-border-sm"/> : <i className="bi bi-file-earmark-pdf"/>}
                      </button>
                      <button className="btn btn-outline-danger btn-sm btn-icon" title="Excluir" onClick={()=>{ setDeleteTarget(c); setShowDelete(true); }} disabled={!canEdit}><i className="bi bi-trash3"/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="pagination-wrapper">
          <span className="pagination-info">
            {pagination.total===0?"Nenhum registro":`Exibindo ${(page-1)*PER_PAGE+1}–${Math.min(page*PER_PAGE,pagination.total)} de ${pagination.total} registros`}
          </span>
          <nav><ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page===1?"disabled":""}`}><button className="page-link" onClick={()=>setPage(p=>p-1)}><i className="bi bi-chevron-left"/></button></li>
            {Array.from({length:totalPages},(_,i)=>(
              <li key={i+1} className={`page-item ${page===i+1?"active":""}`}><button className="page-link" onClick={()=>setPage(i+1)}>{i+1}</button></li>
            ))}
            <li className={`page-item ${page===totalPages?"disabled":""}`}><button className="page-link" onClick={()=>setPage(p=>p+1)}><i className="bi bi-chevron-right"/></button></li>
          </ul></nav>
        </div>
      </div>

      <CondominoModal show={showModal} onClose={()=>{setShowModal(false);setModalError(null);}} onSave={handleSave}
        editData={editData} saving={saving} serverError={modalError}/>
      <DeleteModal show={showDelete} onClose={()=>setShowDelete(false)} onConfirm={handleDelete}
        itemName={deleteTarget?.nmCompleto} loading={deleting}/>
      <ImportarCondominosModal show={showImport} onClose={()=>setShowImport(false)} onImported={()=>{carregar();carregarStats();}}/>
      <AniversariantesModal show={showAniv} onClose={()=>setShowAniv(false)}/>
    </div>
  );
}
